"use client";

import { useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import Image from "next/image";
import { toWebp } from "@/utils/common";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

interface TechBuildProps {
  data?: any;
}

const TechBuild = ({ data }: TechBuildProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = (data?.cards || []).map((card: any) => {
    const imageUrl = card?.image?.data?.attributes?.url;
    const hoverUrl = card?.hover_image?.data?.attributes?.url || imageUrl;

    return {
      id: card.id,
      title: card.display_title,
      description: card.title,
      image: imageUrl ? toWebp(`${baseUrl}${imageUrl}`) : undefined,
      hoverImage: hoverUrl ? toWebp(`${baseUrl}${hoverUrl}`) : undefined,
    };
  });

  const autoplay = (slider: any) => {
    let timeout: ReturnType<typeof setTimeout>;
    let mouseOver = false;

    const clearNextTimeout = () => {
      clearTimeout(timeout);
    };

    const nextTimeout = () => {
      clearTimeout(timeout);

      if (mouseOver) return;

      timeout = setTimeout(() => {
        if (slider.track.details) {
          slider.next();
        }
      }, 3000);
    };

    slider.on("created", () => {
      slider.container.addEventListener("mouseover", () => {
        mouseOver = true;
        clearNextTimeout();
      });

      slider.container.addEventListener("mouseout", () => {
        mouseOver = false;
        nextTimeout();
      });

      nextTimeout();
    });

    slider.on("dragStarted", clearNextTimeout);
    slider.on("animationEnded", nextTimeout);
    slider.on("updated", nextTimeout);
    slider.on("destroyed", clearNextTimeout);
  };

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: true,
      slides: {
        perView: 1,
        spacing: 8,
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
    },
    [autoplay],
  );

  return (
    <section className="relative overflow-hidden bg-background-white py-[56px] md:py-[80px] xl:py-[100px]">
      <div className="container mx-auto px-4">
        {/* Title */}
        <h2 className="mx-auto xl:mb-[40px] mb-[32px] text-center lg:text-[48px] lg:leading-[46px] text-[30px] leading-[39px] font-bold tracking-[1px] text-primary-text">
          {data?.title}
        </h2>

        {/* Mobile Slider */}
        <div className="md:hidden">
          <div ref={sliderRef} className="keen-slider">
            {slides.map((slide: any, index: number) => (
              <div key={slide.id ?? index} className="keen-slider__slide group">
                <div className="border border-[#B0D9F7] w-full h-[422px] p-[40px_20px] flex flex-col items-center justify-start bg-contain bg-no-repeat bg-bottom bg-[url('/images/bg-bottom-card.png')] [@media(hover:none)]:bg-[url('/images/bg-hover-bottom-card.png')] [@media(hover:hover)_and_(pointer:fine)]:group-hover:bg-[url('/images/bg-hover-bottom-card.png')]">
                  <div className="flex flex-col items-center justify-center mb-[32px]">
                    {slide.image && (
                      <Image
                        src={slide.image}
                        alt={slide.title}
                        width={72}
                        height={72}
                        className="w-[72px] h-[72px] object-contain block"
                      />
                    )}
                  </div>

                  <p className=" xl:text-[24px] xl:leading-[31.2px] text-[20px] leading-[26px] text-center font-bold text-primary-text mb-[12px]">
                    {slide.title}
                  </p>

                  <p className="mt-[24px] text-center xl:text-[14px] xl:leading-[19.6px] text-[12px] leading-[16.8px] font-normal text-secondary-text">
                    {slide.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Dots */}
          <div className="mt-[24px] flex justify-center gap-2">
            {slides.map((_: any, idx: number) => (
              <button
                key={idx}
                onClick={() => instanceRef.current?.moveToIdx(idx)}
                className={`h-[4px] rounded-full transition-all ${
                  currentSlide === idx
                    ? "w-[140px] bg-gradient-to-r from-[#8ABC00] to-[#0086E6]"
                    : "w-[16px] bg-blue-100"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Tablet & Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {slides.map((slide: any, index: number) => (
            <div key={slide.id ?? index} className="group">
              <div className="w-full h-[377px] backdrop-blur-[5px] p-[20px_20px] border border-[#B0D9F7] flex flex-col items-center justify-start bg-contain bg-no-repeat bg-bottom bg-[url('/images/bg-bottom-card.png')] [@media(hover:none)]:bg-[url('/images/bg-hover-bottom-card.png')] [@media(hover:hover)_and_(pointer:fine)]:group-hover:bg-[url('/images/bg-hover-bottom-card.png')]">
                <div className="flex flex-col items-center justify-center mb-[32px]">
                  {slide.image && (
                    <Image
                      src={slide.image}
                      alt={slide.title}
                      width={72}
                      height={72}
                      className="w-[62px] h-[62px] object-contain xl:block hidden group-hover:hidden opacity-[0.5] group-hover:opacity-100"
                    />
                  )}

                  {slide.hoverImage && (
                    <Image
                      src={slide.hoverImage}
                      alt={slide.title}
                      width={72}
                      height={72}
                      className="w-[62px] h-[62px] object-contain xl:hidden block group-hover:xl:block opacity-100 xl:opacity-[0.5] group-hover:opacity-100"
                    />
                  )}
                </div>

                <p className="line-clamp-3 xl:text-[24px] xl:leading-[31.2px] text-[20px] 2xl:min-h-[70px] xl:min-h-[90px] min-h-[60px] leading-[26px] text-center font-bold text-primary-text ">
                  {slide.title}
                </p>

                <p className="line-clamp-4  mt-[24px] text-center xl:text-[14px] xl:leading-[19.6px] text-[12px] leading-[16.8px] font-normal text-secondary-text text-balance">
                  {slide.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechBuild;
